import classNames from 'classnames';
import React, {
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type { Position } from 'react-avatar-editor';
import AvatarEditor from 'react-avatar-editor';
import { useDropzone } from 'react-dropzone';
import type { Placement } from '@floating-ui/react-dom';
import imageIcon from '../icons/non-prefilled/28x28/dropzoneImage.svg';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import type { Banner } from './BannerModalContent';
import Button from './Button';
import FloatingArrow from './FloatingArrow';
import * as styles from './ImageEditor.module.scss';
import SVGIcon from './SVGIcon';
import Tether, { useTetherContext } from './Tether';

const CLICK_EVENTS = new WeakSet<Event>();
const JPEG_CONTENT_TYPE = 'image/jpeg';

// These are common image file types supported in the web.
// See: https://developer.mozilla.org/en-US/docs/Web/Media/Guides/Formats/Image_types#common_image_file_types
// For this reason, these are the ones that `react-avatar-editor` supports.
export const IMAGE_EDITOR_ACCEPT = [
  'image/apng',
  'image/avif',
  'image/gif',
  'image/jpeg',
  'image/png',
  'image/svg+xml',
  'image/webp',
].reduce((acc, type) => ({ ...acc, [type]: [] }), {});

function getFileFromCanvas(canvas: HTMLCanvasElement) {
  return new Promise<File | null>((resolve) => {
    canvas.toBlob(
      (blob) => {
        const file = blob
          ? new File([blob], 'banner.jpg', { type: JPEG_CONTENT_TYPE })
          : null;

        resolve(file);
      },
      JPEG_CONTENT_TYPE,
      0.9,
    );
  });
}

function useOnWheel(
  ref: React.RefObject<HTMLElement | null>,
  onWheel: (e: WheelEvent) => void,
  opts: { skip: boolean },
) {
  const onWheelRef = useRef(onWheel);

  useLayoutEffect(() => {
    onWheelRef.current = onWheel;
  });

  useEffect(() => {
    if (opts.skip) return undefined;

    const container = ref.current;

    // We can't use React's `onWheel` prop because of a backwards-incompatible
    // change in Chrome 73: https://github.com/facebook/react/issues/14856
    const handleWrapperWheel = (e: WheelEvent) => {
      e.preventDefault();
      onWheelRef.current(e);
    };

    container?.addEventListener('wheel', handleWrapperWheel, {
      passive: false,
    });

    return () => {
      container?.removeEventListener('wheel', handleWrapperWheel);
    };
  }, [opts.skip, ref]);
}

const DEFAULT_SCALE = 1.0;

interface ImageEditorDropZoneProps {
  onDropImage(file: File): void;
  onClick?: () => void;
  children: React.ReactNode;
  disabled?: boolean;
  positioning?: boolean;
}

const ImageEditorDropZone = ({
  onDropImage,
  onClick,
  children,
  positioning,
  disabled,
}: ImageEditorDropZoneProps) => {
  const { getRootProps, isDragAccept } = useDropzone({
    accept: IMAGE_EDITOR_ACCEPT,
    multiple: false,
    maxSize: 25 * 1000 * 1000,
    disabled: disabled || positioning,
    onDrop([file]) {
      if (file) {
        onDropImage(file);
      }
    },
  });

  return (
    <Button
      // We can't just disable the button when positioning because it will
      // prevent the user from dragging the image while positioning.
      onClick={positioning ? undefined : onClick}
      onMouseDown={(e) => {
        CLICK_EVENTS.add(e.nativeEvent);
      }}
      onTouchStart={(e) => {
        CLICK_EVENTS.delete(e.nativeEvent);
      }}
      className={classNames(styles.dropZoneButton, {
        [styles.isPositioning]: positioning,
      })}
      disabled={disabled}
    >
      <div
        {...getRootProps()}
        className={classNames(styles.dropZoneContainer, {
          [styles.isHidden]: !isDragAccept,
        })}
      >
        <div className={styles.dropZone}>
          <SVGIcon className={styles.dropZoneIcon} size={28}>
            {imageIcon}
          </SVGIcon>
          <div>Drop an image to upload</div>
        </div>
        {children}
      </div>
    </Button>
  );
};

interface ImageEditorRootProps {
  positioning?: boolean;
  value: Banner;
  src: string | null;
  width: number;
  height: number;
  dropZoneDisabled?: boolean;
  onImageLoad?: (file: File | null) => void;
  onScaleChange?: (scale: number) => void;
  onPositionChange?: (position: Position) => void;
  onDropImage: (file: File) => void;
  onDropZoneClick?: () => void;
}

export interface ImageEditorRootRef {
  getFile(): Promise<File | null>;
}

const ImageEditorRoot = React.forwardRef<
  ImageEditorRootRef,
  ImageEditorRootProps
>(
  (
    {
      src,
      value,
      width,
      height,
      positioning,
      dropZoneDisabled,
      onImageLoad,
      onScaleChange,
      onPositionChange,
      onDropImage,
      onDropZoneClick,
    },
    ref,
  ) => {
    const [scale, setScale] = useState<{ value: number; touched: boolean }>({
      value: DEFAULT_SCALE,
      touched: false,
    });

    const onScaleChangeRef = useRef(onScaleChange);
    useLayoutEffect(() => {
      onScaleChangeRef.current = onScaleChange;
    });

    useEffect(() => {
      if (scale.touched) {
        onScaleChangeRef.current?.(scale.value);
      }
    }, [scale]);

    useEffect(() => {
      setScale({ value: DEFAULT_SCALE, touched: false });
    }, [value]);

    const avatarEditorRef = useRef<AvatarEditor>(null);

    const getFile = () => {
      const editor = avatarEditorRef.current;
      if (editor) {
        return getFileFromCanvas(editor.getImageScaledToCanvas());
      }
      return Promise.resolve(null);
    };

    useImperativeHandle(ref, () => ({
      getFile,
    }));

    const editorContainerRef = useRef<HTMLDivElement>(null);

    useOnWheel(
      editorContainerRef,
      (e) => {
        setScale((current) => ({
          value: Math.max(current.value + e.deltaY / 1000, 1.0),
          touched: true,
        }));
      },
      {
        skip: !positioning || value.image === src,
      },
    );

    return (
      <ImageEditorDropZone
        onDropImage={onDropImage}
        disabled={dropZoneDisabled}
        positioning={positioning}
        onClick={onDropZoneClick}
      >
        <div
          className={styles.container}
          style={{ aspectRatio: `${width}/${height}` }}
        >
          {value.image === src && !positioning ? (
            <div
              className={styles.image}
              style={{ backgroundImage: `url(${value.image})` }}
            />
          ) : (
            <div
              className={classNames(styles.editorContainer, {
                [styles.disabled]: !positioning,
              })}
              ref={editorContainerRef}
            >
              <AvatarEditor
                border={0}
                crossOrigin="anonymous"
                image={value.image}
                ref={avatarEditorRef}
                scale={scale.value}
                height={height}
                width={width}
                onImageReady={async () => {
                  if (!onImageLoad) return;
                  const file = await getFile();
                  onImageLoad(file);
                }}
                onPositionChange={onPositionChange}
              />
            </div>
          )}
        </div>
      </ImageEditorDropZone>
    );
  },
);

const DragZoomInfoPill = () => {
  return (
    <div className={styles.dragZoomInfoPill}>Drag & zoom image to move</div>
  );
};

const Arrow = () => {
  const { setArrow, arrowData, placement } = useTetherContext();

  return (
    <FloatingArrow
      ref={setArrow}
      placement={placement}
      arrowData={arrowData}
      padding={2}
    />
  );
};

interface ImageEditorPopOverProps {
  open: boolean;
  onOpenChange: (isOpen: boolean) => void;
  placement: Placement;
  children: React.ReactNode;
  content: React.ReactNode;
}

const ImageEditorPopOver = ({
  open,
  onOpenChange,
  placement,
  children,
  content,
}: ImageEditorPopOverProps) => {
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);

  return (
    <>
      <div ref={setAnchorElement}>{children}</div>
      {open && (
        <Tether
          anchorElement={anchorElement}
          placement={placement}
          flipBehavior="disabled"
          positionedView={
            <ClickOutsideHandler
              className={styles.positionedView}
              onClickOutside={(e) => {
                if (!CLICK_EVENTS.has(e)) {
                  onOpenChange(false);
                }
              }}
            >
              <div className={styles.imageEditorPopOverContent}>{content}</div>
              <Arrow />
            </ClickOutsideHandler>
          }
        />
      )}
    </>
  );
};

export default {
  Root: ImageEditorRoot,
  PopOver: ImageEditorPopOver,
  DragZoomInfoPill,
  DropZone: ImageEditorDropZone,
};
